import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, ChevronUp, Trophy, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface Challenge {
  id: string;
  title: string;
  one_liner: string | null;
  status: string | null;
  difficulty: string | null;
}

interface NestedChallengesSectionProps {
  eventId: string;
  defaultExpanded?: boolean;
}

const NestedChallengesSection: React.FC<NestedChallengesSectionProps> = ({
  eventId,
  defaultExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const { data: challenges, isLoading } = useQuery({
    queryKey: ["event-challenges", eventId],
    staleTime: 60000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenges")
        .select("id, title, one_liner, status, difficulty")
        .eq("event_id", eventId)
        .order("vote_score", { ascending: false });

      if (error) throw error;
      return data as Challenge[];
    },
  });

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case "unclaimed": return "bg-muted text-muted-foreground";
      case "in_progress": return "bg-primary/20 text-primary";
      case "testing": return "bg-yellow-500/20 text-yellow-600";
      case "complete": return "bg-green-500/20 text-green-600";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getStatusLabel = (status: string | null) => {
    switch (status) {
      case "unclaimed": return "Open";
      case "in_progress": return "In Progress";
      case "testing": return "Testing";
      case "complete": return "Complete";
      default: return "Open";
    }
  };

  if (isLoading) {
    return (
      <div className="pt-3 border-t border-border mt-3">
        <Skeleton className="h-8 w-40 mb-2" />
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  if (!challenges || challenges.length === 0) {
    return null;
  }

  return (
    <div className="pt-3 border-t border-border mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full text-left"
      >
        <Trophy className="h-4 w-4" />
        <span>Challenges for this Event ({challenges.length})</span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 ml-auto" />
        ) : (
          <ChevronDown className="h-4 w-4 ml-auto" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-2">
          {challenges.map((challenge) => (
            <div
              key={challenge.id}
              className={cn(
                "p-3 rounded-lg bg-muted/30 border border-border",
                "hover:border-primary/30 transition-colors"
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {challenge.title}
                    </h4>
                    <Badge className={cn("text-xs", getStatusColor(challenge.status))}>
                      {getStatusLabel(challenge.status)}
                    </Badge>
                  </div>
                  {challenge.one_liner && (
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {challenge.one_liner}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className="shrink-0"
                >
                  <Link to={`/challenges?id=${challenge.id}`}>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
            </div>
          ))}
          
          <Button
            variant="outline"
            size="sm"
            asChild
            className="w-full mt-2"
          >
            <Link to="/challenges">
              View All Challenges
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default NestedChallengesSection;
